import { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  TextField,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { PageTitle } from '../components/PageTitle';
import { AnimatedBox } from '../components/AnimatedBox';
import { dashboardService, aggregateDailyData } from '../service/firebase/dashboardService';
import { DailyChartData } from '../type';

// ── 숫자 포맷 ──
const formatKRW = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) =>
  new Intl.NumberFormat('ko-KR').format(value);

// ── 색상 팔레트 ──
const COLORS = {
  coupang: '#E98302',
  linkprice: '#4CAF50',
  cancel: '#E53935',
  commission: '#1976D2',
};

const PIE_COLORS = ['#E98302', '#4CAF50', '#1976D2', '#9C27B0'];

// ── 날짜 유틸 ──
const toDateStr = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

type PresetKey = 'today' | 'yesterday' | '7d' | '30d' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisYear';

interface Preset {
  key: PresetKey;
  label: string;
  getRange: () => [string, string];
}

const PRESETS: Preset[] = [
  {
    key: 'today',
    label: '오늘',
    getRange: () => {
      const d = toDateStr(new Date());
      return [d, d];
    },
  },
  {
    key: 'yesterday',
    label: '어제',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const s = toDateStr(d);
      return [s, s];
    },
  },
  {
    key: 'thisWeek',
    label: '이번 주',
    getRange: () => {
      const now = new Date();
      const day = now.getDay();
      const mon = new Date(now);
      mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      return [toDateStr(mon), toDateStr(now)];
    },
  },
  {
    key: '7d',
    label: '최근 7일',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return [toDateStr(start), toDateStr(end)];
    },
  },
  {
    key: 'thisMonth',
    label: '이번 달',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return [toDateStr(start), toDateStr(now)];
    },
  },
  {
    key: '30d',
    label: '최근 30일',
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 29);
      return [toDateStr(start), toDateStr(end)];
    },
  },
  {
    key: 'lastMonth',
    label: '지난 달',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return [toDateStr(start), toDateStr(end)];
    },
  },
  {
    key: 'thisYear',
    label: '올해',
    getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      return [toDateStr(start), toDateStr(now)];
    },
  },
];

// ── 차트 커스텀 툴팁 ──
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, maxWidth: 280 }}>
      <Typography variant="caption" fontWeight={600}>
        {label}
      </Typography>
      {payload.map((entry: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: entry.color }}>
            {entry.name}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {typeof entry.value === 'number' && entry.value > 1000
              ? formatKRW(entry.value)
              : formatNumber(entry.value)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

// ── 스켈레톤 ──
const ChartSkeleton = ({ height = 320 }: { height?: number }) => (
  <Paper sx={{ p: 2.5 }}>
    <Skeleton width="40%" height={24} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" width="100%" height={height} />
  </Paper>
);

// ── 기간 내 합계 계산 ──
function sumPeriod(data: DailyChartData[]) {
  return data.reduce(
    (acc, d) => ({
      coupangRevenue: acc.coupangRevenue + d.coupangRevenue,
      coupangOrders: acc.coupangOrders + d.coupangOrders,
      coupangCommission: acc.coupangCommission + d.coupangCommission,
      linkpriceRevenue: acc.linkpriceRevenue + d.linkpriceRevenue,
      linkpriceRewards: acc.linkpriceRewards + d.linkpriceRewards,
      linkpriceCommission: acc.linkpriceCommission + d.linkpriceCommission,
      coupangCancels: acc.coupangCancels + d.coupangCancels,
      coupangCancelAmount: acc.coupangCancelAmount + d.coupangCancelAmount,
      linkpriceCancels: acc.linkpriceCancels + d.linkpriceCancels,
      linkpriceCancelAmount: acc.linkpriceCancelAmount + d.linkpriceCancelAmount,
    }),
    {
      coupangRevenue: 0, coupangOrders: 0, coupangCommission: 0,
      linkpriceRevenue: 0, linkpriceRewards: 0, linkpriceCommission: 0,
      coupangCancels: 0, coupangCancelAmount: 0,
      linkpriceCancels: 0, linkpriceCancelAmount: 0,
    }
  );
}

// ── 메인 대시보드 ──
const Dashboard = () => {
  const [activePreset, setActivePreset] = useState<PresetKey | null>('30d');
  const defaultRange = PRESETS.find((p) => p.key === '30d')!.getRange();
  const [startDate, setStartDate] = useState(defaultRange[0]);
  const [endDate, setEndDate] = useState(defaultRange[1]);
  const [enterprise, setEnterprise] = useState<string>('');

  const applyPreset = useCallback((preset: Preset) => {
    const [s, e] = preset.getRange();
    setStartDate(s);
    setEndDate(e);
    setActivePreset(preset.key);
  }, []);

  const handleDateChange = useCallback((type: 'start' | 'end', value: string) => {
    if (type === 'start') setStartDate(value);
    else setEndDate(value);
    setActivePreset(null);
  }, []);

  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['dashboardRaw'],
    queryFn: () => dashboardService.fetchRawData(),
    staleTime: 5 * 60 * 1000,
  });

  const dailyData = useMemo(
    () => (rawData ? aggregateDailyData(rawData, enterprise || null) : []),
    [rawData, enterprise]
  );

  const filteredData = useMemo(
    () => dailyData.filter((d) => d.date >= startDate && d.date <= endDate),
    [dailyData, startDate, endDate]
  );

  const periodStats = useMemo(() => sumPeriod(filteredData), [filteredData]);

  const commissionPieData = useMemo(
    () =>
      [
        { name: '쿠팡', value: periodStats.coupangRevenue },
        { name: '링크프라이스', value: periodStats.linkpriceRevenue },
      ].filter((d) => d.value > 0),
    [periodStats]
  );

  return (
    <AnimatedBox>
      {/* ── 헤더 ── */}
      <PageTitle sx={{ mb: 1 }}>대시보드</PageTitle>

      {/* ── 필터 영역 ── */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        {/* 프리셋 칩들 */}
        {PRESETS.map((preset) => (
          <Chip
            key={preset.key}
            label={preset.label}
            onClick={() => applyPreset(preset)}
            variant={activePreset === preset.key ? 'filled' : 'outlined'}
            color={activePreset === preset.key ? 'primary' : 'default'}
            size="small"
            sx={{ fontWeight: activePreset === preset.key ? 600 : 400 }}
          />
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* 직접 날짜 선택 */}
        <TextField
          type="date"
          size="small"
          label="시작일"
          value={startDate}
          onChange={(e) => handleDateChange('start', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 150 }}
        />
        <Typography variant="body2" color="text.secondary">~</Typography>
        <TextField
          type="date"
          size="small"
          label="종료일"
          value={endDate}
          onChange={(e) => handleDateChange('end', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 150 }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* 기업 필터 */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>기업</InputLabel>
          <Select
            value={enterprise}
            label="기업"
            onChange={(e) => setEnterprise(e.target.value)}
          >
            <MenuItem value="">전체</MenuItem>
            {(rawData?.enterprises ?? []).map((ent) => (
              <MenuItem key={ent.id} value={ent.id}>{ent.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          데이터를 불러오는 중 오류가 발생했습니다: {String((error as any)?.message || error)}
        </Alert>
      )}

      {/* 데이터 없을 때만 안내 */}
      {!isLoading && !error && filteredData.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          선택한 기간({startDate} ~ {endDate})에 해당하는 데이터가 없습니다.
          {dailyData.length ? ` 전체 데이터는 ${dailyData.length}건 있습니다.` : ''}
        </Alert>
      )}

      {/* ── 커미션 요약 카드 ── */}
      {isLoading ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Skeleton width="30%" height={28} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 6, md: 3 }} key={i}>
                <Skeleton variant="rounded" height={72} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {formatKRW(
                periodStats.coupangRevenue + periodStats.linkpriceRevenue
                - periodStats.coupangCancelAmount - periodStats.linkpriceCancelAmount
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              총 커미션 (취소 차감)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box sx={{ borderLeft: `3px solid ${COLORS.coupang}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.secondary">쿠팡 커미션</Typography>
                <Typography variant="h6" fontWeight={700}>{formatKRW(periodStats.coupangRevenue)}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box sx={{ borderLeft: `3px solid ${COLORS.linkprice}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.secondary">링크프라이스 커미션</Typography>
                <Typography variant="h6" fontWeight={700}>{formatKRW(periodStats.linkpriceRevenue)}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box sx={{ borderLeft: `3px solid ${COLORS.cancel}`, pl: 1.5 }}>
                <Typography variant="caption" color="text.secondary">쿠팡 취소</Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  -{formatKRW(periodStats.coupangCancelAmount)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Box sx={{ borderLeft: `3px solid ${COLORS.cancel}`, pl: 1.5, opacity: 0.85 }}>
                <Typography variant="caption" color="text.secondary">링크프라이스 취소</Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  -{formatKRW(periodStats.linkpriceCancelAmount)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── 커미션 추이 & 파이 ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          {isLoading ? (
            <ChartSkeleton />
          ) : (
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                일별 커미션 추이
              </Typography>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 10000).toFixed(0)}만`} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="coupangRevenue" name="쿠팡 커미션" stroke={COLORS.coupang} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="linkpriceRevenue" name="링크프라이스 커미션" stroke={COLORS.linkprice} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          {isLoading ? (
            <ChartSkeleton height={320} />
          ) : (
            <Paper sx={{ p: 2.5, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                채널별 커미션 비중
              </Typography>
              {commissionPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={commissionPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }: any) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      {commissionPieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => formatKRW(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                  <Typography color="text.secondary">커미션 데이터가 없습니다</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* ── 취소 & 주문 건수 차트 ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          {isLoading ? (
            <ChartSkeleton height={280} />
          ) : (
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                일별 취소 추이
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="coupangCancels" name="쿠팡 취소" stroke={COLORS.coupang} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="linkpriceCancels" name="링크프라이스 취소" stroke={COLORS.linkprice} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="coupangCancelAmount" name="쿠팡 취소금액" stroke={COLORS.cancel} strokeWidth={1} strokeDasharray="5 5" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          {isLoading ? (
            <ChartSkeleton height={280} />
          ) : (
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                일별 주문 건수
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Bar dataKey="coupangOrders" name="쿠팡 주문" fill={COLORS.coupang} radius={[2, 2, 0, 0]} />
                  <Bar dataKey="linkpriceRewards" name="링크프라이스 리워드" fill={COLORS.linkprice} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* ── 요약 테이블 ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              쿠팡 일별 요약
            </Typography>
            <TableContainer sx={{ maxHeight: 340, height: 'auto', border: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>날짜</TableCell>
                    <TableCell align="right">주문수</TableCell>
                    <TableCell align="right">커미션</TableCell>
                    <TableCell align="right">취소</TableCell>
                    <TableCell align="right">취소금액</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 5 }).map((__, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredData.length > 0 ? (
                    [...filteredData].reverse().slice(0, 20).map((row) => (
                      <TableRow key={row.date} hover>
                        <TableCell>{row.date}</TableCell>
                        <TableCell align="right">{formatNumber(row.coupangOrders)}</TableCell>
                        <TableCell align="right">{formatKRW(row.coupangRevenue)}</TableCell>
                        <TableCell align="right" sx={{ color: row.coupangCancels > 0 ? 'error.main' : undefined }}>
                          {row.coupangCancels > 0 ? formatNumber(row.coupangCancels) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: row.coupangCancelAmount > 0 ? 'error.main' : undefined }}>
                          {row.coupangCancelAmount > 0 ? `-${formatKRW(row.coupangCancelAmount)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        데이터가 없습니다
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              링크프라이스 일별 요약
            </Typography>
            <TableContainer sx={{ maxHeight: 340, height: 'auto', border: 'none' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>날짜</TableCell>
                    <TableCell align="right">리워드 수</TableCell>
                    <TableCell align="right">커미션</TableCell>
                    <TableCell align="right">취소</TableCell>
                    <TableCell align="right">취소금액</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 5 }).map((__, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filteredData.length > 0 ? (
                    [...filteredData].reverse().slice(0, 20).map((row) => (
                      <TableRow key={row.date} hover>
                        <TableCell>{row.date}</TableCell>
                        <TableCell align="right">{formatNumber(row.linkpriceRewards)}</TableCell>
                        <TableCell align="right">{formatKRW(row.linkpriceRevenue)}</TableCell>
                        <TableCell align="right" sx={{ color: row.linkpriceCancels > 0 ? 'error.main' : undefined }}>
                          {row.linkpriceCancels > 0 ? formatNumber(row.linkpriceCancels) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ color: row.linkpriceCancelAmount > 0 ? 'error.main' : undefined }}>
                          {row.linkpriceCancelAmount > 0 ? `-${formatKRW(row.linkpriceCancelAmount)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        데이터가 없습니다
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </AnimatedBox>
  );
};

export default Dashboard;
